import { useEffect, useMemo, useRef, useState } from 'react'
import { recruiterApi } from '../services/api'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/Textarea'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, X } from 'lucide-react'

interface SavedForm {
  id: number
  uuid: string
  job_description: string | null
  questions: Array<unknown>
}

const EMAIL_SOURCE = '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}'

function emailsFromText(text: string): string[] {
  const found = text.match(new RegExp(EMAIL_SOURCE, 'g')) || []
  return found.map((email) => email.toLowerCase())
}

function isExactEmail(value: string): boolean {
  return new RegExp(`^${EMAIL_SOURCE}$`, 'i').test(value.trim())
}

function uniqueEmails(emails: string[]): string[] {
  return Array.from(new Set(emails))
}

function formLabel(form: SavedForm): string {
  const description = (form.job_description || 'בלי תיאור משרה').replace(/\s+/g, ' ').trim()
  const short = description.length > 80 ? `${description.slice(0, 80)}...` : description
  return `${short} (${form.questions?.length || 0} שאלות)`
}

export default function SendReadyTestPage() {
  const [forms, setForms] = useState<SavedForm[]>([])
  const [formUuid, setFormUuid] = useState('')
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  const [emailDraft, setEmailDraft] = useState('')
  const [emailDraftError, setEmailDraftError] = useState<string | null>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [fileEmails, setFileEmails] = useState<string[]>([])
  const [fileName, setFileName] = useState('')
  const [subject, setSubject] = useState('הזמנה למבחן')
  const [body, setBody] = useState(
    'שלום,\nהוזמנת למלא מבחן. לפני תחילת המבחן תתבקש להזין שם, טלפון, אימייל ולהעלות קורות חיים.\n\n{{FORM_LINK}}'
  )
  const [loadingForms, setLoadingForms] = useState(true)
  const [readingFile, setReadingFile] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    const loadForms = async () => {
      try {
        const response = await recruiterApi.getForms()
        if (response.status === 'success') {
          setForms(response.forms)
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'שגיאה בטעינת המבחנים'
        setError(message)
      } finally {
        setLoadingForms(false)
      }
    }
    loadForms()
  }, [])

  const emails = useMemo(
    () => uniqueEmails([...recipientEmails, ...fileEmails]),
    [recipientEmails, fileEmails]
  )

  const addRecipientEmails = (nextEmails: string[]) => {
    if (nextEmails.length === 0) return
    setRecipientEmails((current) => uniqueEmails([...current, ...nextEmails]))
    setEmailDraft('')
    setEmailDraftError(null)
  }

  const commitEmailDraft = () => {
    const draft = emailDraft.trim()
    if (!draft) {
      setEmailDraftError(null)
      return true
    }

    const parts = draft.split(/[\s,;]+/).filter(Boolean)
    const valid = parts.filter((part) => isExactEmail(part)).map((part) => part.toLowerCase())
    const invalid = parts.filter((part) => !isExactEmail(part))

    if (invalid.length > 0) {
      setEmailDraftError('כתובת אימייל לא תקינה')
      if (valid.length > 0) {
        setRecipientEmails((current) => uniqueEmails([...current, ...valid]))
        setEmailDraft(invalid.join(' '))
      }
      return false
    }

    addRecipientEmails(valid)
    return true
  }

  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault()
      commitEmailDraft()
      return
    }

    if (event.key === 'Backspace' && emailDraft === '' && recipientEmails.length > 0) {
      event.preventDefault()
      setRecipientEmails((current) => current.slice(0, -1))
    }
  }

  const handleEmailPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text')
    const found = emailsFromText(text)
    if (found.length === 0) return

    const remainder = text.replace(new RegExp(EMAIL_SOURCE, 'g'), '').replace(/[\s,;]+/g, '')
    if (remainder.length > 0) return

    event.preventDefault()
    addRecipientEmails(found)
  }

  const handleFile = async (file: File | undefined) => {
    setError(null)
    setResult(null)
    if (!file) {
      setFileEmails([])
      setFileName('')
      return
    }

    const extension = file.name.split('.').pop()?.toLowerCase()
    setFileName(file.name)

    if (extension === 'csv' || extension === 'txt') {
      const text = await file.text()
      setFileEmails(emailsFromText(text))
      return
    }

    if (extension === 'xlsx') {
      setReadingFile(true)
      try {
        const response = await recruiterApi.parseReadyTestExcel(file)
        setFileEmails(response.emails.map((email) => email.toLowerCase()))
      } catch (err: unknown) {
        setFileEmails([])
        const axiosMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        setError(axiosMessage || 'לא ניתן לקרוא את קובץ האקסל')
      } finally {
        setReadingFile(false)
      }
      return
    }

    setFileEmails([])
    setError('אפשר להעלות קובץ CSV, TXT או XLSX')
  }

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setResult(null)

    if (!formUuid) {
      setError('יש לבחור מבחן')
      return
    }
    if (!commitEmailDraft()) {
      return
    }
    const draftEmails = emailDraft
      .trim()
      .split(/[\s,;]+/)
      .filter((part) => isExactEmail(part))
      .map((part) => part.toLowerCase())
    const emailsToSend = uniqueEmails([...emails, ...draftEmails])
    if (emailsToSend.length === 0) {
      setError('יש להזין לפחות כתובת אימייל אחת')
      return
    }
    if (!subject.trim() || !body.trim()) {
      setError('יש למלא נושא ותוכן')
      return
    }
    if (!body.includes('{{FORM_LINK}}')) {
      setError('התוכן חייב לכלול את {{FORM_LINK}} כדי שהנמען יקבל קישור')
      return
    }

    setSending(true)
    try {
      const response = await recruiterApi.sendReadyTest({
        formUuid,
        emails: emailsToSend,
        subject: subject.trim(),
        body,
        frontendBaseUrl: window.location.origin,
      })
      setResult(
        `נשלחו ${response.sent_count}, נכשלו ${response.failed_count}, דולגו ${response.skipped_count} (כבר קיבלו את המבחן)`
      )
    } catch (err: unknown) {
      const axiosMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(axiosMessage || 'שגיאה בשליחת המיילים')
    } finally {
      setSending(false)
    }
  }

  if (loadingForms) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="bg-background px-4 py-6" dir="rtl">
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-6 text-2xl font-semibold text-gray-900">שליחת מבחן מוכן</h1>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}
        {result && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-4 text-green-700">
            {result}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">בחירת מבחן ונמענים</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-4">
              <div className="space-y-2">
                <Label>מבחן</Label>
                <Select dir="rtl" value={formUuid || undefined} onValueChange={setFormUuid}>
                  <SelectTrigger className="text-right">
                    <SelectValue placeholder="בחר מבחן שמור" />
                  </SelectTrigger>
                  <SelectContent className="text-right">
                    {forms.map((form) => (
                      <SelectItem key={form.uuid} value={form.uuid}>
                        {formLabel(form)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-4">
                <Label htmlFor="recipient-emails" className="mb-2 block text-sm font-medium text-gray-700">
                  כתובות אימייל
                </Label>
                <div
                  className={`flex min-h-36 cursor-text flex-wrap content-start items-start gap-2 rounded-lg border bg-transparent px-3 py-2 transition-all duration-200 focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500 ${
                    emailDraftError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onClick={() => emailInputRef.current?.focus()}
                >
                  {recipientEmails.map((email) => (
                    <Badge key={email} variant="secondary" className="gap-1 pe-1">
                      <span dir="ltr">{email}</span>
                      <button
                        type="button"
                        className="rounded-sm p-0.5 text-secondary-foreground/70 hover:bg-secondary-foreground/10 hover:text-secondary-foreground"
                        aria-label={`הסר ${email}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          setRecipientEmails((current) => current.filter((item) => item !== email))
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <input
                    id="recipient-emails"
                    ref={emailInputRef}
                    value={emailDraft}
                    onChange={(event) => {
                      setEmailDraft(event.target.value)
                      setEmailDraftError(null)
                    }}
                    onKeyDown={handleEmailKeyDown}
                    onPaste={handleEmailPaste}
                    onBlur={commitEmailDraft}
                    dir="ltr"
                    placeholder={recipientEmails.length === 0 ? 'הקלד כתובת ולחץ Enter' : ''}
                    className="h-7 min-w-40 flex-1 bg-transparent text-right text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                {emailDraftError && <p className="mt-1 text-sm text-red-600">{emailDraftError}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-file">או העלה קובץ CSV, TXT או Excel</Label>
                <Input
                  id="email-file"
                  type="file"
                  accept=".csv,.txt,.xlsx"
                  onChange={(event) => handleFile(event.target.files?.[0])}
                />
                {fileName && (
                  <p className="text-sm text-gray-600">
                    {readingFile ? 'קורא קובץ...' : `${fileName}: ${fileEmails.length} כתובות`}
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-700">סה״כ נמענים ייחודיים: {emails.length}</p>

              <div className="space-y-2">
                <Label htmlFor="subject">נושא</Label>
                <Input id="subject" value={subject} onChange={(event) => setSubject(event.target.value)} />
              </div>

              <Textarea
                label="תוכן"
                rows={6}
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />

              <Button type="submit" className="w-full" disabled={sending || readingFile}>
                {sending ? (
                  <>
                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                    שולח...
                  </>
                ) : (
                  'שלח מבחן'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
